export type BookingSource = "live" | "preview";

export type BookingMode = "online" | "in_person";

export type Offer = {
  id: string;
  slug: string;
  title: string;
  eyebrow: string;
  description: string;
  durationSummary: string;
  priceSummary: string;
  setting: string;
  accent: "navy" | "gold" | "sage" | "stone";
  billingEntity?: "seminare" | "coaching" | "praxis";
};

export type OfferVariant = {
  id: string;
  offerId: string;
  slug: string;
  label: string;
  durationMinutes: number;
  priceCents: number | null;
  currency: string;
  priceLabel: string;
  isDefault: boolean;
};

export type BookingLocation = {
  id: string;
  slug: string;
  name: string;
  mode: BookingMode;
  description: string;
  addressSummary: string;
  timezone: string;
};

export type OfferBookingLocation = {
  offerId: string;
  locationId: string;
};

export type BookingSlot = {
  id: string;
  offerId: string;
  offerVariantId: string;
  bookingLocationId: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  location: string;
};

export type BookingCatalog = {
  source: BookingSource;
  offers: Offer[];
  variants: OfferVariant[];
  locations: BookingLocation[];
  offerLocations: OfferBookingLocation[];
  slots: BookingSlot[];
};

export type BookingContact = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  note: string;
  consent: boolean;
};

export type BookingRequest = {
  offer: Offer;
  variant: OfferVariant;
  location: BookingLocation;
  slot: BookingSlot;
  contact: BookingContact;
};

export type BookingResult = {
  reference: string;
  managementToken?: string;
  source: BookingSource;
};

export type ContactErrors = Partial<Record<keyof BookingContact, string>>;

export const previewOffers: Offer[] = [
  {
    id: "preview-orientation",
    slug: "kennenlernen-orientierung",
    title: "Kennenlernen & Orientierung",
    eyebrow: "Erster persönlicher Termin",
    description:
      "Ein fokussiertes erstes Gespräch, um dein Anliegen kennenzulernen und gemeinsam den passenden nächsten Schritt zu klären.",
    durationSummary: "30 Minuten",
    priceSummary: "Preis folgt",
    setting: "Berlin oder Oranienburg · Präsenz",
    accent: "stone",
  },
  {
    id: "preview-coaching",
    slug: "coaching-persoenliches-gespraech",
    title: "Coaching & persönliches Gespräch",
    eyebrow: "Klärung, Entscheidung & Entwicklung",
    description:
      "Ein persönlicher Raum für eine konkrete Lebens-, Beziehungs- oder berufliche Frage – vom klaren Impuls bis zum vertiefenden Prozess.",
    durationSummary: "60 Minuten bis 4 Stunden",
    priceSummary: "ab 250 €",
    setting: "Berlin oder Oranienburg · Präsenz",
    accent: "sage",
  },
  {
    id: "preview-bodywork",
    slug: "bodywork-praxis",
    title: "Bodywork & Praxis",
    eyebrow: "Körperarbeit & Regulation",
    description:
      "Körperorientierte und naturheilkundliche Begleitung. Für einen ruhigen, stimmigen Prozess beginnt dieses Format bei 90 Minuten.",
    durationSummary: "ab 90 Minuten",
    priceSummary: "ab 300 €",
    setting: "Berlin oder Oranienburg · Präsenz",
    accent: "navy",
  },
];

function createVariant(
  id: string,
  offerId: string,
  durationMinutes: number,
  priceCents: number | null,
  isDefault = false,
): OfferVariant {
  return {
    id,
    offerId,
    slug: `${durationMinutes}-minuten`,
    label: formatDuration(durationMinutes),
    durationMinutes,
    priceCents,
    currency: "EUR",
    priceLabel: formatPrice(priceCents, "EUR"),
    isDefault,
  };
}

export const previewVariants: OfferVariant[] = [
  createVariant("preview-orientation-30", "preview-orientation", 30, null, true),
  createVariant("preview-coaching-60", "preview-coaching", 60, 25_000, true),
  createVariant("preview-coaching-90", "preview-coaching", 90, 37_500),
  createVariant("preview-coaching-120", "preview-coaching", 120, 50_000),
  createVariant("preview-coaching-180", "preview-coaching", 180, 75_000),
  createVariant("preview-coaching-240", "preview-coaching", 240, 100_000),
  createVariant("preview-bodywork-90", "preview-bodywork", 90, 30_000, true),
  createVariant("preview-bodywork-120", "preview-bodywork", 120, 40_000),
  createVariant("preview-bodywork-180", "preview-bodywork", 180, 60_000),
  createVariant("preview-bodywork-240", "preview-bodywork", 240, 80_000),
];

export const previewBerlinLocation: BookingLocation = {
  id: "preview-berlin",
  slug: "berlin-weissensee",
  name: "Berlin-Weißensee",
  mode: "in_person",
  description: "Persönlich vor Ort im Schörling Cityrefugium.",
  addressSummary: "Berliner Allee 75, 13088 Berlin",
  timezone: "Europe/Berlin",
};

export const previewOranienburgLocation: BookingLocation = {
  id: "preview-oranienburg",
  slug: "oranienburg",
  name: "Oranienburg",
  mode: "in_person",
  description: "Persönlich vor Ort im Schörling Cityrefugium Oranienburg.",
  addressSummary: "Thälmannstraße 24, 16515 Oranienburg",
  timezone: "Europe/Berlin",
};

export const previewLocations: BookingLocation[] = [
  previewBerlinLocation,
  previewOranienburgLocation,
];

export const previewOfferLocations: OfferBookingLocation[] = previewOffers.flatMap((offer) =>
  previewLocations.map((location) => ({
    offerId: offer.id,
    locationId: location.id,
  })),
);

const previewSlotStartsByLocation: Record<string, string[]> = {
  [previewBerlinLocation.id]: [
    "2026-11-11T09:30:00+01:00",
    "2026-11-11T11:30:00+01:00",
    "2026-11-12T10:00:00+01:00",
    "2026-11-12T14:30:00+01:00",
    "2026-11-13T09:00:00+01:00",
    "2026-11-13T12:00:00+01:00",
  ],
  [previewOranienburgLocation.id]: [
    "2026-11-16T09:30:00+01:00",
    "2026-11-16T11:30:00+01:00",
    "2026-11-17T10:00:00+01:00",
    "2026-11-17T14:30:00+01:00",
    "2026-11-18T09:00:00+01:00",
    "2026-11-18T12:00:00+01:00",
  ],
};

export const previewSlots: BookingSlot[] = previewVariants.flatMap((variant, variantIndex) =>
  previewLocations.flatMap((location) =>
    (previewSlotStartsByLocation[location.id] ?? [])
      .slice(variantIndex % 2, (variantIndex % 2) + 4)
      .map((startsAt, slotIndex) => {
        const start = new Date(startsAt);
        const end = new Date(start.getTime() + variant.durationMinutes * 60_000);

        return {
          id: `${variant.id}-${location.id}-slot-${slotIndex + 1}`,
          offerId: variant.offerId,
          offerVariantId: variant.id,
          bookingLocationId: location.id,
          startsAt,
          endsAt: end.toISOString(),
          timezone: location.timezone,
          location: location.addressSummary,
        };
      }),
  ),
);

export const previewCatalog: BookingCatalog = {
  source: "preview",
  offers: previewOffers,
  variants: previewVariants,
  locations: previewLocations,
  offerLocations: previewOfferLocations,
  slots: previewSlots,
};

export function validateContact(contact: BookingContact): ContactErrors {
  const errors: ContactErrors = {};

  if (!contact.firstName.trim()) {
    errors.firstName = "Bitte den Vornamen eintragen.";
  }

  if (!contact.lastName.trim()) {
    errors.lastName = "Bitte den Nachnamen eintragen.";
  }

  const email = contact.email.trim();
  if (!email) {
    errors.email = "Bitte eine E-Mail-Adresse eintragen.";
  } else if (!/^\S+@\S+\.\S+$/.test(email)) {
    errors.email = "Bitte eine gültige E-Mail-Adresse eintragen.";
  }

  if (!contact.consent) {
    errors.consent = "Bitte die Verarbeitung der Angaben bestätigen.";
  }

  return errors;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} Minuten`;
  }

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0
    ? `${hours} ${hours === 1 ? "Stunde" : "Stunden"}`
    : `${hours} Std. ${remainder} Min.`;
}

export function formatPrice(priceCents: number | null, currency = "EUR"): string {
  if (priceCents === null) {
    return "Preis folgt";
  }
  if (priceCents === 0) {
    return "Kostenfrei";
  }

  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(priceCents / 100);
}

export function formatDate(startsAt: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "Europe/Berlin",
  }).format(new Date(startsAt));
}

export function formatDateCompact(startsAt: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    timeZone: "Europe/Berlin",
  }).format(new Date(startsAt));
}

export function formatTime(startsAt: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Berlin",
  }).format(new Date(startsAt));
}

export function dateKey(startsAt: string): string {
  return new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "Europe/Berlin",
  }).format(new Date(startsAt));
}
